// Cloudflare Web Analytics custom-event wrapper.
//
// The event contract (names + props) is defined in
// `briefs/metrics.md` §"What we count" / "Games played" — that
// brief is the source of truth; this module is the typed
// realization. The wrapper is staged for Phase 1.5 to call
// from the game state machine; it is not yet imported anywhere
// in production code.
//
// Invariants:
// - Returns immediately if the beacon global is unavailable
//   (e.g., script not yet loaded, or blocked by a content
//   blocker). Analytics must never affect gameplay.
// - Defensive: any error thrown by the beacon is swallowed
//   silently. There is deliberately no console output.

export type EventName = "Puzzle Started" | "Puzzle Finished" | "Share Copied";

export interface PuzzleStartedProps {
  game_id: string;
  number: number;
  date: string;
}

export interface PuzzleFinishedProps extends PuzzleStartedProps {
  outcome: "won" | "lost";
}

/** Format the player actually copied / shared. The native-share path
   resolves to whichever payload the OS share-sheet accepted: `image`
   when the system took the PNG file, `text` when it fell back to the
   text-only branch. Per-`(game_id, format)` dedupe lives at the call
   site (`game/state.ts`); this module just dispatches. */
export interface ShareCopiedProps extends PuzzleStartedProps {
  format: "text" | "image";
}

export type TrackedEvent =
  | { name: "Puzzle Started"; props: PuzzleStartedProps }
  | { name: "Puzzle Finished"; props: PuzzleFinishedProps }
  | { name: "Share Copied"; props: ShareCopiedProps };

/**
 * Cloudflare Web Analytics beacon global. The exact dispatch
 * surface for custom events is undocumented in Cloudflare's
 * public developer docs; we implement against a conservative
 * `window.__cfBeacon.dispatch(name, props)` shape and feature-
 * detect at call time so the wrapper stays a no-op when the
 * surface is missing or differs.
 */
interface CfBeacon {
  dispatch?: (name: string, props: Record<string, unknown>) => void;
}

declare global {
  interface Window {
    __cfBeacon?: CfBeacon;
  }
}

export function trackEvent(name: "Puzzle Started", props: PuzzleStartedProps): void;
export function trackEvent(name: "Puzzle Finished", props: PuzzleFinishedProps): void;
export function trackEvent(name: "Share Copied", props: ShareCopiedProps): void;
export function trackEvent(
  name: EventName,
  props: PuzzleStartedProps | PuzzleFinishedProps | ShareCopiedProps,
): void {
  dispatch(name, props as unknown as Record<string, unknown>);
}

function dispatch(name: EventName, props: Record<string, unknown>): void {
  try {
    if (typeof window === "undefined") return;
    const beacon = window.__cfBeacon;
    if (!beacon || typeof beacon.dispatch !== "function") return;
    beacon.dispatch(name, props);
  } catch {
    // Swallow — analytics must never affect gameplay.
  }
}
